// src/pages/servicos/ordensServico/OrdemServicoItemModal.js
import React, { useEffect, useState } from 'react';
import Input from '../../../components/input/Input';
import InputCheck from '../../../components/input/InputCheck';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { useProduto } from '../../../contexts/produtos/ProdutoContext';
import { formatNumeric, normalizeDecimalInputBr, parseNumeric } from '../../../utils/numberUtils';
import ProdutoCad from '../../produtos/produtos/ProdutoCad';

const itemVazio = {
  produtoId: 0,
  xProd: '',
  alteraDescricao: false,
  quantidade: '1,00',
  valorUnitario: '0,00',
  valorDesconto: '0,00',
  autorizadoCliente: true,
  executado: true,
  observacao: '',
};

const OrdemServicoItemModal = ({ show, item, onHide, onSave }) => {
  const [dados, setDados] = useState(itemVazio);
  const [showCadastroProduto, setShowCadastroProduto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const { produtos, showProdutoSearch } = useProduto();
  const { showMessage } = useMessage();
  const editando = Boolean(item);
  const produtoBloqueado = Boolean(dados.id);

  useEffect(() => {
    if (show) {
      setSalvando(false);
      setDados(item ? {
        ...item,
        quantidade: formatNumeric(item.quantidade ?? 1),
        valorUnitario: formatNumeric(item.valorUnitario ?? 0),
        valorDesconto: formatNumeric(item.valorDesconto ?? 0),
      } : { ...itemVazio });
    }
  }, [show, item]);

  const alterar = (campo, valor) => {
    setDados(anterior => ({
      ...anterior,
      [campo]: valor,
    }));
  };

  const selecionarProduto = produto => {
    if (!produto) return;

    setDados(anterior => ({
      ...anterior,
      produtoId: produto.id,
      xProd: produto.descricao,
      alteraDescricao: Boolean(produto.alteraDescricao),
      valorUnitario: formatNumeric(produto.preco || 0),
      tipo: produto.ehServico ? 2 : 1,
    }));
  };

  const buscar = async () => {
    const produto = await showProdutoSearch();
    selecionarProduto(produto);
  };

  const buscarPorCodigo = () => {
    if (!dados.produtoId) {
      setDados(anterior => ({ ...anterior, xProd: '', alteraDescricao: false }));
      return;
    }

    const produto = produtos.find(produtoAtual => (
      produtoAtual.id?.toString() === dados.produtoId.toString()
    ));

    if (!produto) {
      setDados(anterior => ({
        ...anterior,
        produtoId: 0,
        xProd: '',
        alteraDescricao: false,
      }));
      showMessage('Produto ou serviço não encontrado.', 'warning');
      return;
    }

    selecionarProduto(produto);
  };

  const cadastrarProduto = () => {
    setShowCadastroProduto(true);
  };

  const produtoCadastrado = produto => {
    selecionarProduto(produto);
    setShowCadastroProduto(false);
  };

  const total = Math.max(
    (parseNumeric(dados.quantidade) * parseNumeric(dados.valorUnitario))
      - parseNumeric(dados.valorDesconto),
    0,
  );

  const salvar = async () => {
    if (salvando) return;
    if (parseNumeric(dados.valorUnitario) <= 0) {
      showMessage('Informe um valor unitário maior que zero.', 'warning');
      return;
    }

    setSalvando(true);
    try {
      await onSave({
        ...dados,
        quantidade: parseNumeric(dados.quantidade),
        valorUnitario: parseNumeric(dados.valorUnitario),
        valorDesconto: parseNumeric(dados.valorDesconto),
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <ModalContainer show={show} largura="650px">
        <ModalHeader>
          <h5>{editando ? 'Alterar item da ordem de serviço' : 'Adicionar item à ordem de serviço'}</h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Fechar"
            disabled={salvando}
            onClick={onHide}
          />
        </ModalHeader>

        <ModalBody>
          <div className="d-flex flex-wrap align-items-end gap-2">
            <div>
              <Input
                label="Código"
                value={dados.produtoId || ''}
                disabled={produtoBloqueado}
                size={100}
                btnBuscar={produtoBloqueado ? null : buscar}
                btnAdd={produtoBloqueado ? null : cadastrarProduto}
                onChange={evento => alterar('produtoId', evento.target.value.replace(/\D/g, ''))}
                onBlur={buscarPorCodigo}
              />
            </div>
            <div>
              <Input
                label="Descrição do produto ou serviço"
                value={dados.xProd || ''}
                disabled={!dados.alteraDescricao}
                size={430}
                onChange={evento => alterar('xProd', evento.target.value)}
              />
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-end gap-3 mt-2">
            <div>
              <Input
                label="Quantidade"
                type="text"
                value={dados.quantidade}
                size={80}
                style={{ textAlign: 'right' }}
                onChange={evento => alterar(
                  'quantidade',
                  normalizeDecimalInputBr(evento.target.value),
                )}
                onBlur={() => alterar('quantidade', formatNumeric(dados.quantidade))}
              />
            </div>
            <div>
              <Input
                label="Valor unitário"
                type="text"
                value={dados.valorUnitario}
                size={100}
                style={{ textAlign: 'right' }}
                onChange={evento => alterar(
                  'valorUnitario',
                  normalizeDecimalInputBr(evento.target.value),
                )}
                onBlur={() => alterar('valorUnitario', formatNumeric(dados.valorUnitario))}
              />
            </div>
            <div>
              <Input
                label="Desconto"
                type="text"
                value={dados.valorDesconto}
                size={100}
                style={{ textAlign: 'right' }}
                onChange={evento => alterar(
                  'valorDesconto',
                  normalizeDecimalInputBr(evento.target.value),
                )}
                onBlur={() => alterar('valorDesconto', formatNumeric(dados.valorDesconto))}
              />
            </div>
            <div>
              <Input
                label="Total"
                value={formatNumeric(total)}
                disabled
                size={110}
                style={{ textAlign: 'right', fontWeight: 600 }}
              />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 my-3">
            <InputCheck
              id="item-autorizado"
              label="Autorizado pelo cliente"
              checked={dados.autorizadoCliente}
              onChange={evento => alterar('autorizadoCliente', evento.target.checked)}
            />
            <InputCheck
              id="item-executado"
              label="Executado/utilizado"
              checked={dados.executado}
              onChange={evento => alterar('executado', evento.target.checked)}
            />
          </div>

          <Input
            label="Observação"
            value={dados.observacao || ''}
            rows={2}
            size="100%"
            onChange={evento => alterar('observacao', evento.target.value)}
          />
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={salvando}
            onClick={onHide}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!dados.produtoId || !dados.xProd?.trim() || salvando}
            onClick={salvar}
          >
            {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
          </button>
        </ModalFooter>
      </ModalContainer>

      <ProdutoCad
        show={showCadastroProduto}
        onHide={() => setShowCadastroProduto(false)}
        onSaveProduto={produtoCadastrado}
      />
    </>
  );
};

export default OrdemServicoItemModal;
